import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {BundeslandFilterService} from '../../../../service/bundeslandFilter/bundesland-filter.service';
import {SearchFilterService} from '../../../../service/search/search-filter.service';
import {DatastoreService} from '../../../../service/datastore/datastore.service';
import {VorhersageHeute} from '../../../../model/vorhersagen/vorhersage-zehn-tage';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {Subscription} from 'rxjs';
import {MatSort} from '@angular/material/sort';
import {WerteConverterUtil} from '../../../../util/werte-converter-util';
import {OptionalTableColumn} from '../../../../model/common/optional-table-column';
import {filter, map} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';

export class ComputerMosVorhersageTableColumn extends OptionalTableColumn {
  static OPTIONAL_COLUMNS: ComputerMosVorhersageTableColumn[] = [
    new ComputerMosVorhersageTableColumn('id', 'ID'),
    new ComputerMosVorhersageTableColumn('typ', 'Typ'),
  ];
}

@Component({
  selector: 'app-computer-mos',
  templateUrl: './computer-mos.component.html',
  styleUrls: ['./computer-mos.component.css']
})
export class ComputerMosComponent implements OnInit, AfterViewInit {

  displayedColumns = ['ort', 'wettersymbol', 'temperaturMin', 'temperaturMax', 'niederschlag', '24hLink', '3hLink'];

  dataSource = new MatTableDataSource<VorhersageHeute>();

  showDetails: boolean = false;

  isLoading = false;

  additionalColumns: ComputerMosVorhersageTableColumn[] = ComputerMosVorhersageTableColumn.OPTIONAL_COLUMNS;

  header: string;

  date: Date = new Date();
  private subscription$: Subscription;

  set vorhersageDaten(vorhersage24hHeute: VorhersageHeute[]) {
    this.dataSource = new MatTableDataSource<VorhersageHeute>();
    this.dataSource.data = vorhersage24hHeute;
    this.dataSource.filterPredicate = this.getFilterPredicate();
  }

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild('table', {read: ElementRef}) tableRef: ElementRef;

  constructor(public bundeslandFilterService: BundeslandFilterService,
              private searchFilterService: SearchFilterService,
              public datastoreService: DatastoreService,
              public werteConverter: WerteConverterUtil,
              private router: Router) {
    this.bundeslandFilterService.onFilterUpdated.subscribe(laender => {
      this.isLoading = true;
    });

    this.router.events.pipe(filter((rs): rs is NavigationEnd => rs instanceof NavigationEnd))
      .subscribe(event => {
        if (event.id === 1 && event.url === event.urlAfterRedirects) {
          this.refreshData();
        }
      });
  }

  ngAfterViewInit() {
    this.setUpTable();
  }

  private setUpTable() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = this.getSortingAccessor();
    this.dataSource.sort = this.sort;
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
  }

  ngOnDestroy() {
    if (this.subscription$ !== undefined) {
      this.subscription$.unsubscribe();
    }
  }

  //Initialisierung aller Filter, wenn ein filter geändert wird, wird das Event getriggert und verarbeitet
  ngOnInit() {
    //Für nested Objects -> Eigene FilterLogik erforderlich
    // this.dataSource.filterPredicate = this.getFilterPredicate();
    this.searchFilterService.onVorhersageSearchFilterUpdated.subscribe(filterValue => {
      this.dataSource.filter = filterValue;
    });

    setTimeout(() => {
      this.isLoading = true;
      this.vorhersageDaten = this.datastoreService.vorhersagen24hHeute;
    });
    //TODO: Replace in async pipe
    this.subscription$ = this.datastoreService
      .onDataChange
      .pipe(map(data => this.vorhersageDaten = data))
      .subscribe(() => {
        this.header = this.getBundesLaenderNamenHeader();
        this.date = new Date();
        this.setUpTable();
        this.isLoading = false;
      });
  }

  //Passt die Header-Überschrift anhand der ausgewählten Bundesländern an -> Hessen, Berlin, Hamburg usw.
  public getBundesLaenderNamenHeader() {
    if (this.datastoreService?.vorhersagen24hHeute?.length > 0) {
      let bundesland = this.datastoreService.vorhersagen24hHeute[0].ort.bundesLand;
      let namen: string = bundesland;
      for (let v of this.datastoreService.vorhersagen24hHeute) {
        namen = namen + '/' + v.ort.bundesLand;
        bundesland = bundesland;
      }
      return Array.from(new Set(namen.split('/'))).toString();
    }
    return '';
  }

  private getSortingAccessor() {
    return (item: VorhersageHeute, property) => {
      switch (property) {
        case 'id':
          return item.ort.id;
        case 'typ':
          return item.ort.typ;
        case 'ort':
          return item.ort.name;
        case 'temperaturMin':
          return item.tag00.werteGanzerTag.luftTemperaturMinimum.wert;
        case 'temperaturMax':
          return item.tag00.werteGanzerTag.luftTemperaturMaximum.wert;
        case 'niederschlag':
          return item.tag00.werteGanzerTag.niederschlagsWahrscheinlichkeit.wert;
        default:
          return item[property];
      }
    };
  }

  refreshData() {
    this.datastoreService.onRefreshVorhersagen.emit(true);
  }

  addColumn(col: ComputerMosVorhersageTableColumn) {
    col.selected ? col.selected = false : col.selected = true;
    if (col.selected) {
      this.displayedColumns.push(col.name);
    } else {
      const index = this.displayedColumns.indexOf(col.name, 0);
      this.displayedColumns.splice(index, 1);
    }
  }

  scrollUp() {
    setTimeout(() => this.tableRef.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'}));
  }

  private getFilterPredicate() {
    return (data: VorhersageHeute, filter: string) => {
      const ortStr = JSON.stringify(data.ort.name).toLowerCase();
      const quelleStr = JSON.stringify(data.ort.quelle).toLowerCase();
      const dataStr = ortStr.concat(quelleStr);
      if (filter.includes(',')) {
        const filters: Array<string> = filter.split(',');
        let match = false;
        for (const f of filters) {
          if (ortStr.indexOf(f) !== -1 || quelleStr.indexOf(f) !== -1) {
            match = true;
          }
        }
        return match;
      }
      return dataStr.indexOf(filter) !== -1;
    };
  }
}
